import { useState, useEffect, useCallback, useRef } from "react";
import { fetchWhatsAppAccountStatus, retryWhatsAppOnboarding } from "../api/whatsappApi";

const POLL_INTERVAL_MS = 3000;

// Statuses where we keep polling — processing is in flight
const POLLING_STATUSES = new Set(["connecting"]);

export function useWhatsAppAccountStatus(tenantId) {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const isFirstFetch = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchOnce = useCallback(async ({ showLoading = false } = {}) => {
    if (!tenantId) return;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const response = await fetchWhatsAppAccountStatus(tenantId);
      const statusData = response?.data?.data ?? null;
      setAccountStatus(statusData);

      // console.log("[useWhatsAppAccountStatus] state updated", {
      //   status: statusData?.status,
      //   stage: statusData?.stage,
      //   availableActions: statusData?.available_actions,
      //   pollingActive: !!pollRef.current,
      // });

      // Stop polling when processing has reached a terminal state
      if (statusData && !POLLING_STATUSES.has(statusData.status)) {
        stopPolling();
        // console.log("[useWhatsAppAccountStatus] polling stopped, terminal status:", statusData.status);
      }
    } catch (err) {
      // console.error("[useWhatsAppAccountStatus] failed", {
      //   status: err?.status,
      //   message: err?.message,
      // });
      setError(err?.message || "Failed to fetch WhatsApp account status.");
      stopPolling(); // Stop polling on error to avoid hammering
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [tenantId, stopPolling]);

  // Start polling loop when status enters "connecting"
  const startPolling = useCallback(() => {
    if (pollRef.current) return; // Already polling
    // console.log("[useWhatsAppAccountStatus] polling started interval_ms=%d", POLL_INTERVAL_MS);
    pollRef.current = setInterval(() => {
      fetchOnce({ showLoading: false });
    }, POLL_INTERVAL_MS);
  }, [fetchOnce]);

  // Manual refetch — resets loading state and re-evaluates polling
  const refetch = useCallback(async () => {
    stopPolling();
    isFirstFetch.current = true;
    setLoading(true);
    // Artificial delay so the user can clearly see the loading state
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await fetchOnce({ showLoading: true });
  }, [fetchOnce, stopPolling]);

  // Initial fetch on mount / tenantId change
  useEffect(() => {
    if (!tenantId) return;
    isFirstFetch.current = true;
    stopPolling();
    fetchOnce({ showLoading: true });

    return () => {
      stopPolling();
    };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to status changes: start polling if status is "connecting"
  useEffect(() => {
    if (!accountStatus) return;
    if (POLLING_STATUSES.has(accountStatus.status)) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [accountStatus?.status, startPolling, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const executeRetry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await retryWhatsAppOnboarding(tenantId);
      const statusData = response?.data?.data ?? null;
      if (statusData) {
        setAccountStatus(statusData);
      } else {
        setAccountStatus((prev) => ({
          ...prev,
          status: "connecting",
          message: "Resuming WhatsApp processing pipeline.",
        }));
      }
      startPolling();
    } catch (err) {
      // console.error("[useWhatsAppAccountStatus] retry failed", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, startPolling]);

  return { accountStatus, loading, error, refetch, executeRetry };
}
